import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { get, ref, update } from "firebase/database";
import { useAuthStore } from "../stores/useAuthStore";
import { useActivityModeStore } from "../stores/useActivityModeStore.jsx";
import { useCan } from "./useCan.js";
import { parseGPX } from "../functions/parseGPX.js";
import { buildHistoryGpx as buildHistoryGpxWithHeartRate, readHistoryGpxNative } from "../utils/historyNative.js";
import { hasCanonicalRecordingMetrics } from "../utils/recordingMetrics.js";
import { db } from "../firebaseConfig";
import { logEvent } from "../stores/useLogsStore.jsx";
import {
  MAX_GAMIFICATION_AVG_SPEED_KMH,
  isAverageSpeedValidForGamification,
} from "../utils/gamificationRules.js";
import { autoAwardPreviousMonthlyRankingXp } from "../utils/monthlyRankingAwards.js";
import { awardRecordingDistanceXp } from "../utils/gamificationXp.js";
import { isHistoryEntryOwnedByUser } from "../utils/historyOwnership.js";
import {
  ACTIVITY_MODE_WALK,
  isActivityModeCompatible,
  normalizeActivityMode,
} from "../utils/activityMode.js";

const UPLOAD_URL = "https://www.trekkin.pt/app/app-upload/upload-history-gpx.php";
const HISTORY_LIST_URL = "https://www.trekkin.pt/app/app-upload/list-history.php";

const parseJson = (raw, fallback) => {
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const isCloudSyncedEntry = (entry) => {
  const syncedFlag = entry?.cloud?.synced === true;
  const hasFile = String(entry?.cloud?.file || "").trim().length > 0;
  const hasUrl = String(entry?.cloud?.url || "").trim().length > 0;
  return syncedFlag || (hasFile && hasUrl);
};

const compactCloudHistoryEntry = (entry) => {
  if (!entry || typeof entry !== "object" || !isCloudSyncedEntry(entry)) return entry;
  const hasGpx = typeof entry.gpx === "string" && entry.gpx.length > 0;
  const hasPoints = Array.isArray(entry.points) && entry.points.length > 0;
  const hasHeartRateSamples =
    Array.isArray(entry?.heartRate?.samples) && entry.heartRate.samples.length > 0;
  if (!hasGpx && !hasPoints && !hasHeartRateSamples) return entry;
  const next = { ...entry };
  delete next.gpx;
  // Depois de o GPX estar confirmado na cloud, os pontos e as amostras BPM
  // deixam de precisar de ocupar a quota reduzida do WebView. As métricas e o
  // cartão permanecem locais; o percurso completo é carregado novamente pelo
  // URL cloud quando o utilizador o abre.
  if (Array.isArray(next.points) && next.points.length) next.points = [];
  if (Array.isArray(next?.heartRate?.samples) && next.heartRate.samples.length) {
    next.heartRate = {
      ...next.heartRate,
      samples: [],
      storedSampleCount: 0,
      samplesAvailableInGpx: true,
    };
  }
  return next;
};

const hasNativeHistoryRef = (entry) => {
  const refs = [
    entry?.native?.gpx,
    entry?.nativeRef,
    entry?.gpxFile,
    entry?.nativeGpx,
  ];
  return (
    refs.some((ref) => {
      if (!ref) return false;
      if (typeof ref === "string") return ref.trim().length > 0;
      return typeof ref === "object" && String(ref.path || "").trim().length > 0;
    }) || entry?.localOnlyNative === true
  );
};

const hasLocalHistoryPayload = (entry) =>
  (Array.isArray(entry?.points) && entry.points.length > 1) ||
  (typeof entry?.gpx === "string" && entry.gpx.trim().length > 0) ||
  hasNativeHistoryRef(entry);

const downsamplePoints = (list, maxPoints) => {
  if (!Array.isArray(list) || list.length <= maxPoints) return list;
  const step = Math.ceil(list.length / maxPoints);
  const sampled = [];
  for (let i = 0; i < list.length; i += step) {
    sampled.push(list[i]);
  }
  const last = list[list.length - 1];
  if (sampled[sampled.length - 1] !== last) sampled.push(last);
  return sampled;
};

const calcStatsFromPoints = (list) => {
  if (!Array.isArray(list) || list.length < 2) {
    return { distKm: 0, totalMin: 0, movingMin: 0, stoppedMin: 0, speed: 0, dPlus: 0, points: list?.length || 0 };
  }
  let distM = 0;
  let movingSec = 0;
  let stoppedSec = 0;
  let dPlus = 0;
  const haversine = (a, b) => {
    const R = 6371000;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const la1 = toRad(a.lat);
    const la2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  for (let i = 1; i < list.length; i++) {
    const a = list[i - 1];
    const b = list[i];
    const t1 = a.ts || a.time;
    const t2 = b.ts || b.time;
    const dt = t1 && t2 ? (t2 - t1) / 1000 : 0;
    if (dt <= 0 || dt > 300) continue;
    const step = haversine(a, b);
    distM += step;
    const kmh = (step / dt) * 3.6;
    if (kmh > 1.2) movingSec += dt;
    else stoppedSec += dt;
    const da = (b.ele ?? b.alt ?? 0) - (a.ele ?? a.alt ?? 0);
    if (da > 0 && da < 15) dPlus += da;
  }
  const distKm = distM / 1000;
  const totalMin = (movingSec + stoppedSec) / 60;
  const movingMin = movingSec / 60;
  const stoppedMin = stoppedSec / 60;
  const speed =
    distKm > 0 && movingSec > 0 ? distKm / (movingSec / 3600) : 0;
  return {
    distKm: Number(distKm.toFixed(2)),
    totalMin: Number(totalMin.toFixed(1)),
    movingMin: Number(movingMin.toFixed(1)),
    stoppedMin: Number(stoppedMin.toFixed(1)),
    speed: Number(speed.toFixed(1)),
    dPlus: Math.round(dPlus),
    points: list.length,
  };
};

const parseEntryTs = (entry) => {
  if (!entry || typeof entry !== "object") return 0;
  const rawCandidates = [
    entry.ts,
    entry.time,
    entry.createdAt,
    entry.updatedAt,
    entry.date,
    entry?.cloud?.mtimeTs,
    entry?.cloud?.mtime,
    entry?.cloud?.syncedAt,
  ];
  for (const value of rawCandidates) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      return value < 1e12 ? value * 1000 : value;
    }
    if (typeof value === "string") {
      const raw = value.trim();
      if (!raw) continue;
      const asNum = Number(raw);
      if (Number.isFinite(asNum) && asNum > 0) {
        return asNum < 1e12 ? asNum * 1000 : asNum;
      }
      const parsed = Date.parse(raw);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  const id = String(entry.id || "");
  const m = id.match(/rec-(\d{10,13})/i);
  if (m && m[1]) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > 0) return n < 1e12 ? n * 1000 : n;
  }
  const cloudFile = String(entry?.cloud?.file || "");
  const fm = cloudFile.match(/(\d{10,13})/);
  if (fm && fm[1]) {
    const n = Number(fm[1]);
    if (Number.isFinite(n) && n > 0) return n < 1e12 ? n * 1000 : n;
  }
  return 0;
};

const formatHistoryDateTime = (ts = Date.now()) => {
  const d = new Date(Number(ts) || Date.now());
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}/${mm}/${yy} - ${hh}:${min}`;
};

const resolveEntryDistKm = (entry) =>
  Number(
    entry?.stats?.distKm ??
      entry?.distanceKm ??
      entry?.distKm ??
      entry?.stats?.distanceKm ??
      0
  ) || 0;

const resolveEntryAvgKmh = (entry, distKm) => {
  const fromStats = Number(
    entry?.stats?.speed ?? entry?.avgSpeedKmh ?? entry?.speedKmh
  );
  if (Number.isFinite(fromStats) && fromStats >= 0) return fromStats;
  const movingMin = Number(entry?.stats?.movingMin ?? entry?.movingMin ?? 0) || 0;
  const totalMin = Number(entry?.stats?.totalMin ?? entry?.totalMin ?? 0) || 0;
  const mins = movingMin > 0 ? movingMin : totalMin;
  if (distKm > 0 && mins > 0) return distKm / (mins / 60);
  return NaN;
};

const isTrackAutoName = (value) => {
  const text = String(value || "").trim().toLowerCase();
  return /^track[_-]?\d+/.test(text);
};

const chooseBestHistoryName = (a, b) => {
  const an = String(a?.name || "").trim();
  const bn = String(b?.name || "").trim();
  if (!an) return bn;
  if (!bn) return an;
  const aAuto = isTrackAutoName(an);
  const bAuto = isTrackAutoName(bn);
  if (aAuto && !bAuto) return bn;
  if (bAuto && !aAuto) return an;
  return an.length >= bn.length ? an : bn;
};

const mergeHistoryEntries = (base, candidate) => {
  const basePts = Array.isArray(base?.points) ? base.points.length : 0;
  const candPts = Array.isArray(candidate?.points) ? candidate.points.length : 0;
  const withMorePoints = candPts > basePts ? candidate : base;
  const withCloud =
    String(base?.cloud?.file || "").trim() && !String(candidate?.cloud?.file || "").trim()
      ? base
      : String(candidate?.cloud?.file || "").trim()
      ? candidate
      : withMorePoints;
  return {
    ...(withMorePoints || {}),
    ...(withCloud || {}),
    ...base,
    ...candidate,
    name: chooseBestHistoryName(base, candidate),
    cloud: {
      ...(base?.cloud || {}),
      ...(candidate?.cloud || {}),
    },
    stats: {
      ...(base?.stats || {}),
      ...(candidate?.stats || {}),
    },
    points:
      Array.isArray(withMorePoints?.points) && withMorePoints.points.length
        ? withMorePoints.points
        : Array.isArray(base?.points)
        ? base.points
        : Array.isArray(candidate?.points)
        ? candidate.points
        : [],
    gpx:
      typeof base?.gpx === "string" && base.gpx
        ? base.gpx
        : typeof candidate?.gpx === "string"
        ? candidate.gpx
        : undefined,
  };
};

const dedupeHistoryEntries = (list) => {
  if (!Array.isArray(list) || list.length < 2) return list;
  const byFile = new Map();
  const keep = [];

  for (const entry of list) {
    if (!entry || typeof entry !== "object") {
      keep.push(entry);
      continue;
    }
    const file = String(entry?.cloud?.file || "").trim();
    if (file) {
      const prev = byFile.get(file);
      if (prev == null) {
        byFile.set(file, keep.length);
        keep.push(entry);
      } else {
        keep[prev] = mergeHistoryEntries(keep[prev], entry);
      }
      continue;
    }
    keep.push(entry);
  }

  // Segunda passagem: colapsa duplicados "Trekkin-xxx" + "track_xxx" sem cloud.file
  const isLikelySameRecording = (a, b) => {
    const ta = parseEntryTs(a);
    const tb = parseEntryTs(b);
    if (!ta || !tb) return false;
    const deltaMs = Math.abs(ta - tb);
    // Casos reportados: 1s de diferença.
    if (deltaMs > 2 * 60 * 1000) return false;

    const da = resolveEntryDistKm(a);
    const db = resolveEntryDistKm(b);
    const distDelta = Math.abs(da - db);
    if (distDelta > 0.12) return false;

    const pa = Number(a?.stats?.points || a?.points?.length || 0);
    const pb = Number(b?.stats?.points || b?.points?.length || 0);
    const pointsMax = Math.max(pa, pb, 1);
    const pointsDelta = Math.abs(pa - pb);
    if (pointsDelta > Math.max(5, Math.round(pointsMax * 0.03))) return false;

    return true;
  };

  const merged = [];
  for (const entry of keep) {
    if (!entry || typeof entry !== "object") {
      merged.push(entry);
      continue;
    }
    const existingIndex = merged.findIndex((it) => {
      if (!it || typeof it !== "object") return false;
      if (isLikelySameRecording(it, entry)) return true;
      const ts = parseEntryTs(entry);
      const dist = resolveEntryDistKm(entry);
      const points = Number(entry?.stats?.points || entry?.points?.length || 0);
      const bucketTs = ts ? Math.floor(ts / (15 * 60 * 1000)) : 0;
      const key = `${bucketTs}|${dist.toFixed(2)}|${points}`;
      const its = parseEntryTs(it);
      const idist = resolveEntryDistKm(it);
      const ipoints = Number(it?.stats?.points || it?.points?.length || 0);
      const ikey = `${its ? Math.floor(its / (15 * 60 * 1000)) : 0}|${idist.toFixed(
        2
      )}|${ipoints}`;
      return ikey === key;
    });
    if (existingIndex >= 0) {
      merged[existingIndex] = mergeHistoryEntries(merged[existingIndex], entry);
    } else {
      merged.push(entry);
    }
  }
  return merged;
};

const findBestLocalHistoryMatch = (list, file, srvTs) => {
  if (!Array.isArray(list) || !file) return null;
  const TOLERANCE_MS = 15 * 60 * 1000;

  for (let i = 0; i < list.length; i += 1) {
    const itemFile = String(list[i]?.cloud?.file || "").trim();
    if (itemFile && itemFile === file) return { entry: list[i], index: i };
  }

  if (!srvTs) return null;

  let best = null;
  let bestDelta = Infinity;
  for (let i = 0; i < list.length; i += 1) {
    const it = list[i];
    const hasCloudFile = String(it?.cloud?.file || "").trim().length > 0;
    if (hasCloudFile) continue;
    const ts = parseEntryTs(it);
    if (!ts) continue;
    const delta = Math.abs(ts - srvTs);
    if (delta <= TOLERANCE_MS && delta < bestDelta) {
      bestDelta = delta;
      best = { entry: it, index: i };
    }
  }
  return best;
};

const buildHistoryGpx = (points, name, heartRate = null) => {
  const uploadPoints = downsamplePoints(points, 20000);
  return buildHistoryGpxWithHeartRate(uploadPoints, name, heartRate);
};

const buildHistoryUploadFilename = (entryName, entryId) => {
  const normalizedBase = String(entryName || "registo")
    .replace(/^trekkin[-_\s]*/i, "")
    .replace(/^trekkin[-_\s]*/i, "")
    .trim();
  const safeName = String(normalizedBase || "registo")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  const safeId = String(entryId || Date.now())
    .replace(/[^a-zA-Z0-9_-]+/g, "")
    .slice(0, 32);
  return `Trekkin-${safeName || "registo"}-${safeId}.gpx`;
};

export default function useHistorySync(options = {}) {
  const enabled = options?.enabled ?? true;
  const user = useAuthStore((s) => s.user);
  const can = useCan();
  const canAutoAwardMonthlyRanking = user?.role === "admin";
  const canUpload =
    user?.role === "admin" ||
    can("PanelHistory", "upload_gpx") ||
    can("PanelNav", "upload_gpx");

  useEffect(() => {
    if (!enabled) return;
    if (!user?.uid) return;
    let active = true;
    const pendingKey = "historyPendingUpload";
    const isNative =
      !!Capacitor?.isNativePlatform?.() ||
      (typeof window !== "undefined" &&
        typeof window.Capacitor !== "undefined" &&
        typeof window.Capacitor.isNativePlatform === "function" &&
        window.Capacitor.isNativePlatform());

    const normalizeHistoryStats = async () => {
      const originalHistoryRaw = localStorage.getItem("historygps") || "[]";
      const local = parseJson(originalHistoryRaw, []);
      const localList = Array.isArray(local) ? local : [];
      let changed = false;
      const next = [];

      for (const item of localList) {
        if (!item) {
          next.push(item);
          continue;
        }
        let points = Array.isArray(item.points) ? item.points : [];
        if (!points.length && typeof item.gpx === "string") {
          try {
            const parsed = await parseGPX(item.gpx);
            points = Array.isArray(parsed?.points) ? parsed.points : [];
          } catch {}
        }
        if (!points.length) {
          try {
            const nativeGpx = await readHistoryGpxNative(item);
            if (nativeGpx) {
              const parsed = await parseGPX(nativeGpx);
              points = Array.isArray(parsed?.points) ? parsed.points : [];
            }
          } catch {}
        }
        if (points.length < 2) {
          const compacted = compactCloudHistoryEntry(item);
          if (compacted !== item) changed = true;
          next.push(compacted);
          continue;
        }
        if (hasCanonicalRecordingMetrics(item)) {
          const compacted = compactCloudHistoryEntry(item);
          if (compacted !== item) changed = true;
          next.push(compacted);
          continue;
        }
        const stats = calcStatsFromPoints(points);
        const prev = item.stats || {};
        const shouldUpdate =
          !Number.isFinite(prev.distKm) ||
          Math.abs(Number(prev.distKm || 0) - stats.distKm) > 0.05 ||
          !Number.isFinite(prev.totalMin) ||
          !Number.isFinite(prev.movingMin) ||
          !Number.isFinite(prev.stoppedMin) ||
          !Number.isFinite(prev.speed) ||
          !Number.isFinite(prev.dPlus);
        if (!shouldUpdate) {
          const compacted = compactCloudHistoryEntry(item);
          if (compacted !== item) changed = true;
          next.push(compacted);
          continue;
        }
        changed = true;
        next.push(compactCloudHistoryEntry({ ...item, stats }));
      }

      if (changed) {
        if ((localStorage.getItem("historygps") || "[]") !== originalHistoryRaw) return;
        const deduped = dedupeHistoryEntries(next);
        localStorage.setItem("historygps", JSON.stringify(deduped));
        window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
      }
    };

    const syncCurrentMonthHistoryFromServer = async (retry = 0) => {
      if (!active || !user?.uid) return;
      // Native fetch also fails while the device is offline. Keep the local
      // history as the source of truth and retry from the online listener.
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      try {
        const nowTs = Date.now();
        const nowDate = new Date(nowTs);
        const monthStartTs = new Date(
          nowDate.getFullYear(),
          nowDate.getMonth(),
          1
        ).getTime();

        const res = await fetch(
          `${HISTORY_LIST_URL}?uid=${encodeURIComponent(user.uid)}`
        );
        const json = await res.json();
        if (!json?.ok || !Array.isArray(json.items)) return;

        const originalHistoryRaw = localStorage.getItem("historygps") || "[]";
        const local = parseJson(originalHistoryRaw, []);
        const localList = Array.isArray(local) ? local : [];
        const next = [...localList];
        const serverFiles = new Set(
          json.items
            .map((it) => String(it?.filename || "").trim())
            .filter(Boolean)
        );
        const localByFile = new Map();
        next.forEach((entry, index) => {
          const file = String(entry?.cloud?.file || "").trim();
          if (!file) return;
          localByFile.set(file, { entry, index });
        });

        let plannedFiles = new Set();
        try {
          const plannedRaw = localStorage.getItem("plannedTrailFiles") || "[]";
          const plannedList = JSON.parse(plannedRaw);
          if (Array.isArray(plannedList)) plannedFiles = new Set(plannedList);
        } catch {}

        let changed = false;
        for (let i = 0; i < next.length; i += 1) {
          const entry = next[i];
          if (!entry || entry?.deletedAt) continue;
          const file = String(entry?.cloud?.file || "").trim();
          if (!file || serverFiles.has(file)) continue;
          if (!isCloudSyncedEntry(entry) || hasLocalHistoryPayload(entry)) continue;
          next[i] = {
            ...entry,
            deletedAt: Date.now(),
            deletedByServerMissing: true,
          };
          changed = true;
        }

        for (const srv of json.items) {
          const file = String(srv?.filename || "").trim();
          if (!file || plannedFiles.has(file)) continue;

          const mtimeSec = Number(srv?.mtime || 0);
          const srvTs =
            Number.isFinite(mtimeSec) && mtimeSec > 0 ? mtimeSec * 1000 : 0;
          if (!srvTs || srvTs < monthStartTs) continue;

          const localMatch =
            localByFile.get(file) ||
            findBestLocalHistoryMatch(next, file, srvTs);
          const existing = localMatch?.entry || null;
          if (existing?.deletedAt) {
            if (localMatch?.index != null) {
              localByFile.set(file, { entry: existing, index: localMatch.index });
            }
            continue;
          }
          const hasUsableData =
            (Array.isArray(existing?.points) && existing.points.length > 1) ||
            typeof existing?.gpx === "string" ||
            Number(existing?.stats?.distKm || 0) > 0;
          if (existing && hasUsableData) {
            const hasSameFile = String(existing?.cloud?.file || "").trim() === file;
            const alreadySynced = existing?.cloud?.synced === true;
            const hasUrl = String(existing?.cloud?.url || "").trim().length > 0;

            if (!hasSameFile || !alreadySynced || !hasUrl) {
              const patched = {
                ...existing,
                uid: existing?.uid || user.uid,
                activityMode: existing?.activityMode || ACTIVITY_MODE_WALK,
                cloud: {
                  ...(existing?.cloud || {}),
                  uid: user.uid,
                  synced: true,
                  file,
                  url: srv?.url || existing?.cloud?.url || null,
                  mtimeTs: srvTs,
                  mtime: mtimeSec,
                  syncedAt: Date.now(),
                },
              };
              if (localMatch) {
                next[localMatch.index] = patched;
                localByFile.set(file, { entry: patched, index: localMatch.index });
              } else {
                const pushIndex = next.push(patched) - 1;
                localByFile.set(file, { entry: patched, index: pushIndex });
              }
              changed = true;
            }
            continue;
          }

          const gpxRes = await fetch(`https://www.trekkin.pt${srv.url}`);
          if (!gpxRes.ok) continue;
          const gpxText = await gpxRes.text();
          if (!gpxText) continue;
          let cloudPoints = [];
          let cloudHeartRate = null;
          try {
            const parsed = await parseGPX(gpxText);
            cloudPoints = Array.isArray(parsed?.points) ? parsed.points : [];
            cloudHeartRate = parsed?.heartRate || null;
          } catch {}
          const cloudStats =
            cloudPoints.length > 1 ? calcStatsFromPoints(cloudPoints) : null;

          const hydrated = {
            ...(existing || {}),
            id: existing?.id || `srv-${file}`,
            uid: existing?.uid || user.uid,
            activityMode: existing?.activityMode || ACTIVITY_MODE_WALK,
            name:
              String(existing?.name || "").trim() ||
              file.replace(/\.gpx$/i, ""),
            ts: parseEntryTs(existing) || srvTs,
            createdAt: parseEntryTs(existing) || srvTs,
            date:
              String(existing?.date || "").trim() ||
              formatHistoryDateTime(srvTs),
            // O GPX permanece na cloud; guardar o texto completo no localStorage
            // esgota rapidamente a quota do Android WebView.
            gpx: undefined,
            points: Array.isArray(existing?.points) ? existing.points : [],
            heartRate: existing?.heartRate || cloudHeartRate || null,
            stats: hasCanonicalRecordingMetrics(existing)
              ? existing.stats
              : { ...(existing?.stats || {}), ...(cloudStats || {}) },
            cloud: {
              ...(existing?.cloud || {}),
              uid: user.uid,
              synced: true,
              file,
              url: srv?.url || existing?.cloud?.url || null,
              mtimeTs: srvTs,
              mtime: mtimeSec,
              syncedAt: Date.now(),
            },
          };

          if (localMatch) {
            next[localMatch.index] = hydrated;
            localByFile.set(file, { entry: hydrated, index: localMatch.index });
          } else {
            const pushIndex = next.push(hydrated) - 1;
            localByFile.set(file, { entry: hydrated, index: pushIndex });
          }
          changed = true;
        }

        if (changed) {
          if ((localStorage.getItem("historygps") || "[]") !== originalHistoryRaw) {
            if (retry < 2) await syncCurrentMonthHistoryFromServer(retry + 1);
            return;
          }
          const deduped = dedupeHistoryEntries(next);
          localStorage.setItem("historygps", JSON.stringify(deduped));
          window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
        }
      } catch {}
    };

    const syncMonthlyRankingFromLocalHistory = async () => {
      if (!active || !user?.uid) return;
      try {
        // Cada atividade é aplicada uma única vez pelo respetivo ID. Isto evita
        // que uma hidratação/compactação temporária do histórico altere o mês.
        {
          const contributionHistory = parseJson(localStorage.getItem("historygps"), []);
          const contributionEntries = Array.isArray(contributionHistory) ? contributionHistory : [];
          for (const entry of contributionEntries) {
            if (!isHistoryEntryOwnedByUser(entry, user.uid, { includeUnowned: false })) continue;
            if (entry?.deletedAt || entry?.gamification?.exclude === true) continue;
            if (entry?.validation?.allowGamification === false || entry?.gamification?.eligible === false) continue;
            const distanceKm = resolveEntryDistKm(entry);
            const entryId = String(entry?.id || entry?.sessionId || "").trim();
            if (!(distanceKm > 0) || !entryId) continue;
            await awardRecordingDistanceXp(user.uid, distanceKm, entryId, {
              activityMode: entry?.activityMode || useActivityModeStore.getState()?.activityMode,
              recordedAt: parseEntryTs(entry) || Date.now(),
              elevationGainM: Number(
                entry?.stats?.dPlus ?? entry?.stats?.elev ?? entry?.elevationGain ?? entry?.elev ?? 0
              ),
            });
          }
        }
        return;

        const nowTs = Date.now();
        const nowDate = new Date(nowTs);
        const monthStartTs = new Date(
          nowDate.getFullYear(),
          nowDate.getMonth(),
          1
        ).getTime();
        const monthKey = `${nowDate.getFullYear()}-${String(
          nowDate.getMonth() + 1
        ).padStart(2, "0")}`;

        const local = parseJson(localStorage.getItem("historygps"), []);
        const localList = Array.isArray(local) ? local : [];
        const activityMode = normalizeActivityMode(
          useActivityModeStore.getState()?.activityMode,
          ACTIVITY_MODE_WALK
        );
        let monthlyKm = 0;
        let hasOwnedHistoryThisMonth = false;

        localList.forEach((entry) => {
          const ts = parseEntryTs(entry);
          if (!ts || ts < monthStartTs) return;
          if (
            !isHistoryEntryOwnedByUser(entry, user.uid, {
              includeUnowned: false,
            })
          ) {
            return;
          }
          if (!isActivityModeCompatible(entry?.activityMode, activityMode)) {
            return;
          }
          hasOwnedHistoryThisMonth = true;
          if (entry?.gamification?.exclude === true) return;
          if (entry?.validation?.allowGamification === false) return;

          const distKm = resolveEntryDistKm(entry);
          if (!(distKm > 0)) return;

          if (entry?.gamification?.eligible === false) return;
          if (entry?.gamification?.eligible !== true) {
            const avgKmh = resolveEntryAvgKmh(entry, distKm);
            if (
              !isAverageSpeedValidForGamification(
                avgKmh,
                MAX_GAMIFICATION_AVG_SPEED_KMH
              )
            ) {
              return;
            }
          }

          monthlyKm += distKm;
        });

        const roundedMonthlyKm = Number(monthlyKm.toFixed(2));
        if (!hasOwnedHistoryThisMonth && roundedMonthlyKm <= 0) return;
        const readExistingDistance = async (path) => {
          try {
            const snap = await get(ref(db, path));
            const n = Number(snap?.val?.());
            return Number.isFinite(n) ? n : 0;
          } catch {
            return 0;
          }
        };

        const privateMonthlyPath =
          activityMode === ACTIVITY_MODE_WALK
            ? `gamification/users/${user.uid}/monthlyStats/${monthKey}/distanceKm`
            : `gamification/users/${user.uid}/monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`;
        const publicMonthlyPath =
          activityMode === ACTIVITY_MODE_WALK
            ? `usersPublic/${user.uid}/gamification/monthlyStats/${monthKey}/distanceKm`
            : `usersPublic/${user.uid}/gamification/monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`;
        const existingPrivateKm = await readExistingDistance(privateMonthlyPath);
        const existingPublicKm = await readExistingDistance(publicMonthlyPath);
        const mergedMonthlyKm = Number(
          Math.max(
            0,
            roundedMonthlyKm,
            existingPrivateKm,
            existingPublicKm
          ).toFixed(2)
        );

        const privateUpdate = {
          [`monthlyStatsByMode/${activityMode}/${monthKey}/monthKey`]: monthKey,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/activityMode`]:
            activityMode,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`]:
            mergedMonthlyKm,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/updatedAt`]: nowTs,
          updatedAt: nowTs,
        };
        if (activityMode === ACTIVITY_MODE_WALK) {
          privateUpdate[`monthlyStats/${monthKey}/monthKey`] = monthKey;
          privateUpdate[`monthlyStats/${monthKey}/distanceKm`] = mergedMonthlyKm;
          privateUpdate[`monthlyStats/${monthKey}/updatedAt`] = nowTs;
        }

        const publicUpdate = {
          [`monthlyStatsByMode/${activityMode}/${monthKey}/monthKey`]: monthKey,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/activityMode`]: activityMode,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/distanceKm`]: mergedMonthlyKm,
          [`monthlyStatsByMode/${activityMode}/${monthKey}/updatedAt`]: nowTs,
          updatedAt: nowTs,
        };
        if (activityMode === ACTIVITY_MODE_WALK) {
          publicUpdate[`monthlyStats/${monthKey}/monthKey`] = monthKey;
          publicUpdate[`monthlyStats/${monthKey}/distanceKm`] = mergedMonthlyKm;
          publicUpdate[`monthlyStats/${monthKey}/updatedAt`] = nowTs;
        }

        const rootUpdate = {};
        Object.entries(privateUpdate).forEach(([path, value]) => {
          rootUpdate[`gamification/users/${user.uid}/${path}`] = value;
        });
        Object.entries(publicUpdate).forEach(([path, value]) => {
          rootUpdate[`usersPublic/${user.uid}/gamification/${path}`] = value;
        });
        await update(ref(db), rootUpdate);
      } catch (err) {
        console.warn("Falha ao sincronizar monthlyStats do histórico:", err);
      }
    };

    const autoAwardMonthlyRankingXp = async () => {
      if (!active || !user?.uid) return;
      if (!canAutoAwardMonthlyRanking) return;
      try {
        await autoAwardPreviousMonthlyRankingXp({
          db,
          actorUid: user.uid,
          actorLabel: "app-auto",
        });
      } catch (err) {
        console.warn("Falha no fecho automático do ranking mensal:", err);
      }
    };

    const syncPendingHistory = async () => {
      if (!active) return;
      if (!canUpload) {
        logEvent("warn", "HISTORY_AUTO_SYNC_SKIPPED", {
          reason: "upload_not_allowed",
          uid: user?.uid || null,
          role: user?.role || null,
          plan: user?.plan || null,
        });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        logEvent("info", "HISTORY_AUTO_SYNC_SKIPPED", {
          reason: "offline",
          uid: user?.uid || null,
        });
        return;
      }
      const local = parseJson(localStorage.getItem("historygps"), []);
      const localList = Array.isArray(local) ? local : [];
      const now = Date.now();
      const STALE_UPLOAD_LOCK_MS = 8 * 60 * 1000;

      let unlockedStale = false;
      const unlockedList = localList.map((it) => {
        if (!it || typeof it !== "object") return it;
        if (isCloudSyncedEntry(it) || it?.cloud?.uploading !== true) return it;
        const startedAt = Number(it?.cloud?.uploadStartedAt || 0);
        if (!startedAt || now - startedAt > STALE_UPLOAD_LOCK_MS) {
          unlockedStale = true;
          return {
            ...it,
            cloud: {
              ...(it?.cloud || {}),
              uploading: false,
              uploadStartedAt: startedAt || null,
            },
          };
        }
        return it;
      });
      if (unlockedStale) {
        localStorage.setItem("historygps", JSON.stringify(unlockedList));
        window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
      }

      const isUploadingNow = (it) => {
        if (it?.cloud?.uploading !== true) return false;
        const startedAt = Number(it?.cloud?.uploadStartedAt || 0);
        if (!startedAt) return false;
        return now - startedAt <= STALE_UPLOAD_LOCK_MS;
      };

      const pending = unlockedList.filter((it) => {
        if (it?.deletedAt) return false;
        if (!it || isCloudSyncedEntry(it) || isUploadingNow(it)) return false;
        if (
          !isHistoryEntryOwnedByUser(it, user.uid, {
            includeUnowned: false,
          })
        ) {
          return false;
        }
        const hasPoints = Array.isArray(it.points) && it.points.length > 1;
        const hasGpx = typeof it.gpx === "string";
        return hasPoints || hasGpx || hasNativeHistoryRef(it);
      });
      if (!pending.length) return;

      logEvent("info", "HISTORY_AUTO_SYNC_PENDING", {
        uid: user?.uid || null,
        count: pending.length,
        ids: pending.map((it) => String(it?.id || "").slice(0, 40)).filter(Boolean),
        platform: isNative ? window?.Capacitor?.getPlatform?.() || "native" : "web",
      });

      for (const entry of pending.slice(0, 2)) {
        const sameEntry = (it) => {
          if (!it || typeof it !== "object") return false;
          const idA = String(entry?.id || "").trim();
          const idB = String(it?.id || "").trim();
          if (idA && idB && idA === idB) return true;

          const fileA = String(entry?.cloud?.file || "").trim();
          const fileB = String(it?.cloud?.file || "").trim();
          if (fileA && fileB && fileA === fileB) return true;

          const urlA = String(entry?.cloud?.url || "").trim();
          const urlB = String(it?.cloud?.url || "").trim();
          if (urlA && urlB && urlA === urlB) return true;

          const tsA = parseEntryTs(entry);
          const tsB = parseEntryTs(it);
          if (tsA && tsB && Math.abs(tsA - tsB) <= 1000) {
            const nameA = String(entry?.name || "").trim();
            const nameB = String(it?.name || "").trim();
            if (!nameA || !nameB || nameA === nameB) return true;
          }
          return false;
        };

        const retryKey =
          String(entry?.id || "").trim() ||
          String(entry?.cloud?.file || "").trim() ||
          String(parseEntryTs(entry) || "");

        const markUploading = (flag) => {
          try {
            const rawList = parseJson(localStorage.getItem("historygps"), []);
            if (!Array.isArray(rawList)) return;
            const patched = rawList.map((it) =>
              sameEntry(it)
                ? {
                    ...it,
                    cloud: {
                      ...(it?.cloud || {}),
                      uploading: !!flag,
                      uploadStartedAt: flag ? Date.now() : it?.cloud?.uploadStartedAt,
                    },
                  }
                : it
            );
            localStorage.setItem("historygps", JSON.stringify(patched));
          } catch {}
        };

        const state = parseJson(localStorage.getItem(pendingKey), {});
        const last = Number(state?.[retryKey] || 0);
        if (Date.now() - last < 2 * 60 * 1000) continue;
        state[retryKey] = Date.now();
        localStorage.setItem(pendingKey, JSON.stringify(state));
        markUploading(true);

        const entryPoints = Array.isArray(entry?.points) ? entry.points : [];
        const hasEnoughPoints = entryPoints.length > 1;
        let gpxText =
          typeof entry.gpx === "string" && entry.gpx.trim().length > 0
            ? entry.gpx
            : "";
        // Native history keeps the complete GPX outside localStorage. Prefer it
        // over the compact preview points so cloud history retains the full run.
        if (!gpxText) {
          try {
            const nativeGpx = await readHistoryGpxNative(entry);
            if (nativeGpx) gpxText = nativeGpx;
          } catch {}
        }
        if (!gpxText && hasEnoughPoints) {
          gpxText = buildHistoryGpx(entryPoints, entry.name, entry.heartRate);
        }
        if (!gpxText) {
          markUploading(false);
          logEvent("warn", "HISTORY_AUTO_SYNC_NO_GPX", {
            uid: user?.uid || null,
            entryId: entry?.id || null,
            hasPoints: hasEnoughPoints,
            points: entryPoints.length,
            hasNativeRef: hasNativeHistoryRef(entry),
            localOnlyNative: entry?.localOnlyNative === true,
          });
          continue;
        }
        const gpxBlob = new Blob([gpxText], { type: "application/gpx+xml" });
        const forcedFilename =
          String(entry?.cloud?.file || "").trim() ||
          buildHistoryUploadFilename(entry?.name, entry?.id);
        const form = new FormData();
        form.append("uid", user.uid);
        form.append("filename", forcedFilename);
        form.append("file", gpxBlob, forcedFilename);

        try {
          const res = await fetch(UPLOAD_URL, { method: "POST", body: form });
          const raw = await res.text();
          let data = null;
          try {
            data = JSON.parse(raw);
          } catch {
            logEvent("warn", "HISTORY_AUTO_SYNC_BAD_RESPONSE", {
              uid: user?.uid || null,
              entryId: entry?.id || null,
              status: res.status,
              ok: res.ok,
              raw: String(raw || "").slice(0, 300),
            });
          }
          if (!data?.ok || !data?.file || !data?.url) {
            markUploading(false);
            logEvent("warn", "HISTORY_AUTO_SYNC_UPLOAD_REJECTED", {
              uid: user?.uid || null,
              entryId: entry?.id || null,
              status: res.status,
              ok: res.ok,
              response: data || null,
            });
            continue;
          }
          const latest = parseJson(localStorage.getItem("historygps"), []);
          const latestList = Array.isArray(latest) ? latest : [];
          const next = latestList.map((it) => {
            if (!sameEntry(it)) return it;
            const syncedEntry = {
              ...it,
              uid: it?.uid || user.uid,
              cloud: {
                ...(it?.cloud || {}),
                uid: user.uid,
                file: data.file,
                url: data.url,
                synced: true,
                syncedAt: Date.now(),
                uploading: false,
              },
            };
            delete syncedEntry.gpx;
            return syncedEntry;
          });
          localStorage.setItem("historygps", JSON.stringify(next));
          window.dispatchEvent(new CustomEvent("trekkin:history-updated"));
          logEvent("info", "HISTORY_AUTO_SYNC_OK", {
            uid: user?.uid || null,
            entryId: entry?.id || null,
            file: data.file,
          });
          const pendingState = parseJson(localStorage.getItem(pendingKey), {});
          delete pendingState[retryKey];
          localStorage.setItem(pendingKey, JSON.stringify(pendingState));
        } catch (err) {
          markUploading(false);
          logEvent("warn", "HISTORY_AUTO_SYNC_FETCH_ERROR", {
            uid: user?.uid || null,
            entryId: entry?.id || null,
            error: err?.message || String(err),
          });
        }
      }
    };

    const handleHistoryUpdated = () => {
      normalizeHistoryStats().then(() => {
        syncMonthlyRankingFromLocalHistory();
        autoAwardMonthlyRankingXp();
        if (canUpload) syncPendingHistory();
      });
    };

    syncCurrentMonthHistoryFromServer().then(() => {
      normalizeHistoryStats().then(() => {
        syncMonthlyRankingFromLocalHistory();
        autoAwardMonthlyRankingXp();
        if (canUpload) syncPendingHistory();
      });
    });
    window.addEventListener("trekkin:history-updated", handleHistoryUpdated);
    const handleOnline = () => {
      syncCurrentMonthHistoryFromServer().then(() => {
        normalizeHistoryStats().then(() => {
          syncMonthlyRankingFromLocalHistory();
          autoAwardMonthlyRankingXp();
          if (canUpload) syncPendingHistory();
        });
      });
    };
    window.addEventListener("online", handleOnline);
    const interval = window.setInterval(handleOnline, 5 * 60 * 1000);

    return () => {
      active = false;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("trekkin:history-updated", handleHistoryUpdated);
      if (interval) window.clearInterval(interval);
    };
  }, [enabled, canAutoAwardMonthlyRanking, canUpload, user?.uid]);
}
